import { ErrorComponent } from '@openx/components-core/lib/Error/Error';
import { FailureImage } from '@openx/components-core/lib/Error/pictures/Failure';
import { LazyLoadable } from '@openx/components-core/lib/LazyLoadable/LazyLoadable';
import type { Auth } from 'firebase/auth';
import { useEffect } from 'react';
import { createBrowserRouter, Navigate, useLocation, useRouteError } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Config } from './configs/ConfigProviders';
import { getRollbar } from './modules/analytics/rollbar';
import { AquaAccessGuard } from './modules/aqua/AquaAccessGuard';
import { AquaRouteGuard } from './modules/aqua/AquaRouteGuard';
import { buildAquaUiRedirectPath } from './modules/aqua/buildAquaUiRedirectPath';
import { AudienceDetailsPage } from './modules/audiences/AudienceDetails/AudienceDetailsPage';
import { AudiencesPage } from './modules/audiences/AudiencesPage';
import { CreateAudiencePage } from './modules/audiences/CreateAudiencePage';
import { EditAudiencePage } from './modules/audiences/EditAudiencePage';
import { ForgotPassword } from './modules/auth/forgotPassword/ForgotPassword';
import { Login } from './modules/auth/login/Login';
import { PageNotFound } from './modules/auth/PageNotFound';
import { UserManagement } from './modules/auth/userManagement/UserManagement';
import { CreateCreativePage } from './modules/creatives/CreateCreativePage';
import { CreativeDetailsPage } from './modules/creatives/CreativeDetailsPage';
import { CreativesListPage } from './modules/creatives/CreativesListPage';
import { EditCreativePage } from './modules/creatives/EditCreativePage';
import { Dashboard } from './modules/dashboard/Dashboard';
import { DealInsightsPage } from './modules/deal-insights/DealInsightsPage';
import { DealInsightsV2Page } from './modules/deal-insights-v2/DealInsightsV2Page';
import { DealPromptPage } from './modules/deal-prompt/DealPromptPage';
import { DealDetailsPage } from './modules/deals/DealDetailsPage';
import { DealsListPage } from './modules/deals/DealsListPage';
import { EditDealPage } from './modules/deals/EditDealPage';
import { DealsSensitiveCategoriesPage } from './modules/deals-sensitive-categories/DealsSensitiveCategoriesPage';
import { DirectDealsDetailsPage } from './modules/direct-deals/DirectDealsDetailsPage';
import { DirectDealsListPage } from './modules/direct-deals/DirectDealsListPage';
import { CuratedDealBuyerDiscountsFormPage } from './modules/discounts/CuratedDealBuyerDiscountsFormPage';
import { CuratedDealBuyerDiscountsPage } from './modules/discounts/CuratedDealBuyerDiscountsPage';
import { HouseholdInsightsPage } from './modules/household-insights/HouseholdInsightsPage';
import { PackageDetailsPage } from './modules/inventory/packages/PackageDetailsPage';
import { PackageFormPage } from './modules/inventory/packages/PackageFormPage';
import { PackagesListPage } from './modules/inventory/packages/PackagesListPage';
import { InventoryListDetailsPage } from './modules/inventoryLists/InventoryListDetailsPage';
import { InventoryListFormPage } from './modules/inventoryLists/InventoryListFormPage';
import { InventoryListsListPage } from './modules/inventoryLists/InventoryListsListPage';
import { OrganizationDetailsPage } from './modules/organizations/OrganizationDetailsPage';
import { OrganizationFormPage } from './modules/organizations/OrganizationFormPage';
import { OrganizationsListPage } from './modules/organizations/OrganizationsListPage';
import { ProviderDetailsPage } from './modules/providers/ProviderDetailsPage';
import { ProviderFormPage } from './modules/providers/ProviderFormPage';
import { ProvidersListPage } from './modules/providers/ProvidersListPage';
import { LalFormPage } from './modules/segments/lal/components/LalFormPage';
import { LalSegmentDetailsPage } from './modules/segments/lal/LalSegmentDetailsPage';
import { SegmentsListPage } from './modules/segments/SegmentsListPage';
import { SettingsPage } from './modules/settings/SettingsPage';
import { TaxonomyListPage } from './modules/taxonomy/TaxonomyListPage';
import { Terms } from './modules/terms/Terms';
import { TermsAcceptance } from './modules/terms/TermsAcceptance';
import { UserDetailsPage } from './modules/users/UserDetailsPage';
import { UserFormPage } from './modules/users/UserFormPage';
import { UsersListPage } from './modules/users/UsersListPage';

const EmbeddedSandbox = LazyLoadable(() => import('./modules/graphql/EmbeddedSandbox'), 'EmbeddedSandbox');

function ErrorBoundary() {
  const error = useRouteError();
  const reloadPage = (): void => {
    window.location.reload();
  };

  const rollbar = getRollbar();
  if (rollbar) {
    if (error instanceof Error) {
      rollbar.error('Router ErrorBoundary', error);
    } else {
      rollbar.error('Router ErrorBoundary', { error });
    }
  }

  return (
    <ErrorComponent
      title="Ooops!"
      titlePrefix="OpenXSelect"
      subtitle="Something went wrong. Please refresh your browser."
      actions={[
        {
          children: 'Refresh',
          color: 'secondary',
          'data-test': 'button-refresh',
          onClick: reloadPage,
          variant: 'contained',
        },
      ]}
      PictureComponent={FailureImage}
      // className={this.props.className}
      data-test="error-refresh"
    />
  );
}

function DealPromptLegacyRedirect() {
  const { search, hash } = useLocation();
  return <Navigate replace to={{ pathname: '/deal-prompt', search, hash }} />;
}

// Orders and Line Items were removed from oa-ui in favor of aqua-ui. aqua-ui
// is a separate deployed app (not part of this router), so reaching it requires a
// full-origin navigation rather than react-router's <Navigate> — the same mechanism
// the "Direct" nav link already uses (see Sidenav's RouterMethod.crossApp handler).
// Deep links to a specific order/line-item (list, detail, edit) are forwarded to
// their aqua-ui equivalent; unsupported sub-paths (e.g. an order's history tab)
// land on the object's base detail page instead of a dead end.
function AquaAppRedirect() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    window.location.replace(`${buildAquaUiRedirectPath(pathname)}${search}${hash}`);
  }, [pathname, search, hash]);
  return null;
}

export function Fallback() {
  return <p>Performing initial data load</p>;
}

export const getRouter = (auth: Auth) =>
  createBrowserRouter(
    [
      {
        // Route-level HydrateFallback for initial data load
        HydrateFallback: Fallback,
        children: [
          {
            Component: Dashboard,
            children: [
              {
                Component: DealsListPage, // Deal List
                index: true,
              },
              {
                Component: EditDealPage, // Deal Create
                path: 'create/*',
              },
              {
                Component: EditDealPage, // Deal Edit
                path: ':dealUid/edit/*',
              },
              {
                Component: DealDetailsPage, // Deal Details
                path: ':dealUid/*',
              },
            ],
            path: '/deals',
          },
          {
            Component: Dashboard,
            path: 'deals-sensitive-categories/*',
            children: [
              {
                index: true,
                element: <Navigate to="proposed" replace />,
              },
              {
                path: '*',
                Component: DealsSensitiveCategoriesPage, // Deals Sensitive Categories Management
              },
            ],
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: AudiencesPage, // Audience List
                index: true,
              },
              {
                Component: CreateAudiencePage, // Audience Create
                path: 'create/*',
              },
              {
                Component: EditAudiencePage, // Audience Edit
                path: ':audienceUid/edit/*',
              },
              {
                Component: AudienceDetailsPage,
                path: ':audienceUid/*',
              },
            ],
            path: '/audiences',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: PackagesListPage, // packages List
                index: true,
              },
              {
                Component: PackagesListPage, // packages curated
                path: 'curated',
              },
              {
                Component: PackageFormPage, // Package Create
                path: 'create/*',
              },
              {
                Component: PackageFormPage, // Package Edit
                path: ':packageId/edit/*',
              },
              {
                Component: PackageDetailsPage, // Package Details
                path: ':packageId/*',
              },
            ],
            path: '/inventory/packages',
          },
          {
            Component: Dashboard,
            children: [
              {
                // `/segments` alone no longer matches child `path: '*'` after dropping the
                // parent `/*` splat for v7_relativeSplatPath — redirect to the default tab.
                element: <Navigate to="audience" replace />,
                index: true,
              },
              {
                Component: SegmentsListPage, // Segments List (audience + lookalike tabs)
                path: '*',
              },
              {
                Component: LalSegmentDetailsPage, // LAL Segments Details
                path: 'lookalike/:lalSegmentUid/*',
              },
              {
                Component: LalFormPage, // LAL Segment Create
                path: 'lookalike/create/*',
              },
              {
                Component: LalFormPage, // LAL Segment Edit
                path: 'lookalike/:lalSegmentUid/edit/*',
              },
            ],
            path: '/segments',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: InventoryListsListPage,
                index: true,
              },
              {
                Component: InventoryListFormPage,
                path: 'create/*',
              },
              {
                Component: InventoryListFormPage,
                path: ':inventoryListId/edit/*',
              },
              {
                Component: InventoryListDetailsPage,
                path: ':inventoryListId/details',
              },
            ],
            path: '/inventory-lists/',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: UsersListPage,
                index: true,
              },
              {
                Component: UserDetailsPage,
                path: ':userId/details',
              },
              {
                Component: UserFormPage,
                path: 'create/*',
              },
              {
                Component: UserFormPage,
                path: ':userId/edit/*',
              },
            ],
            path: '/users',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: TaxonomyListPage, // Taxonomy List
                index: true,
              },
            ],
            path: '/taxonomy',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: DealInsightsPage,
                index: true,
                path: '*',
              },
            ],
            path: '/deal-insights',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: DealInsightsV2Page,
                index: true,
                path: '*',
              },
            ],
            path: '/deal-insights-v2',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: HouseholdInsightsPage,
                index: true,
              },
            ],
            path: '/household-insights',
          },
          {
            Component: Dashboard,
            children: [
              {
                element: <Navigate to={'/deals'} replace={true} />,
                index: true,
              },
              {
                Component: UserManagement,
                path: 'user-management',
              },
            ],
            path: '/',
          },
          {
            Component: EmbeddedSandbox,
            path: '/sandbox',
          },
          {
            Component: EmbeddedSandbox,
            path: '/graphiql',
          },
          {
            Component: Login,
            path: '/login',
          },
          {
            Component: ForgotPassword,
            path: '/forgot-password',
          },
          {
            Component: UserManagement,
            path: '/set-password',
          },
          {
            Component: Terms,
            path: '/terms',
          },
          {
            Component: TermsAcceptance,
            path: '/terms-acceptance',
          },
          {
            Component: PageNotFound,
            path: '*',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: OrganizationsListPage,
                index: true,
              },
              {
                Component: OrganizationDetailsPage,
                path: ':organizationId/details',
              },
              {
                Component: OrganizationFormPage,
                path: 'create/*',
              },
              {
                Component: OrganizationFormPage,
                path: ':organizationId/edit/*',
              },
            ],
            path: '/organizations',
          },
          {
            Component: Dashboard,
            children: [
              { Component: ProvidersListPage, index: true },
              {
                Component: ProviderDetailsPage,
                path: ':providerId/*',
              },
              {
                Component: ProviderFormPage,
                path: 'create/*',
              },
              {
                Component: ProviderFormPage,
                path: ':providerId/edit/*',
              },
            ],
            path: '/providers',
          },

          {
            Component: Dashboard,
            children: [
              {
                Component: DirectDealsListPage,
                index: true,
              },
              {
                Component: DirectDealsDetailsPage,
                path: ':publisherId/details',
              },
            ],
            path: '/direct-deals',
          },
          {
            Component: Dashboard,
            children: [
              {
                Component: CuratedDealBuyerDiscountsPage,
                index: true,
              },
              {
                Component: CuratedDealBuyerDiscountsFormPage,
                path: 'create/*',
              },
              {
                Component: CuratedDealBuyerDiscountsPage,
                path: ':curatedDealBuyerDiscountUid/details/*',
              },
              {
                Component: CuratedDealBuyerDiscountsFormPage,
                path: ':curatedDealBuyerDiscountUid/edit/*',
              },
            ],
            path: '/discounts',
          },
          { Component: Dashboard, children: [{ Component: SettingsPage, index: true }], path: '/settings' },
          {
            Component: Dashboard,
            path: '/aqua',
            children: [
              {
                Component: AquaRouteGuard,
                children: [
                  { Component: AquaAppRedirect, index: true },
                  { Component: AquaAppRedirect, path: 'orders/*' },
                ],
              },
              {
                Component: AquaAccessGuard,
                children: [
                  { Component: AquaAppRedirect, path: 'line-items/*' },
                  {
                    children: [
                      { Component: CreativesListPage, index: true },
                      {
                        Component: CreateCreativePage,
                        path: 'create/*',
                      },
                      {
                        Component: EditCreativePage,
                        path: ':creativeId/edit',
                      },
                      {
                        Component: CreativeDetailsPage,
                        path: ':creativeId/*',
                      },
                    ],
                    path: 'creatives',
                  },
                ],
              },
            ],
          },
          {
            Component: Dashboard,
            path: '/deal-prompt',
            children: [{ Component: DealPromptPage, index: true }],
          },
          {
            path: '/discovery-studio',
            element: <DealPromptLegacyRedirect />,
          },
        ],
        element: <Config auth={auth} />,
        errorElement: <ErrorBoundary />,
      },
    ],
    {
      basename: '/',
    }
  );

export const Routes = ({ auth }: { auth: Auth }) => {
  return <RouterProvider router={getRouter(auth)} useTransitions={false} />;
};
